import { Badge, Box, Button, Flex, Group, Text } from '@mantine/core';
import { IconCheck, IconMail } from '@tabler/icons-react';
import { useConnectOutlook } from 'src/common/hooks/useConnectOutlook';
import { OutlookDisconnectButton } from 'src/common/components/OutlookDisconnectButton';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { OutlookEmailContext } from './EmailDraftCard';

type Props = {
  outlook?: OutlookEmailContext;
  // True once the conversation has moved on past this card (a newer user message exists).
  expired?: boolean;
};

export function ConnectOutlookCard({ outlook, expired }: Props) {
  const { connectOutlook, isConnecting } = useConnectOutlook();

  const handleConnect = () =>
    connectOutlook(outlook?.conversationId, {
      pageSetsContentMeta: outlook?.pageSetsContentMeta,
      autoResume: true,
    });

  // Expiry is checked first so a card the conversation has moved past collapses to the muted
  // row whatever the connection status says.
  if (expired) {
    return (
      <Box bg="gray.1" p="sm" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
        <Text size="textXs" c="gray.6">
          Outlook connection request — no longer active
        </Text>
      </Box>
    );
  }

  if (outlook?.connected) {
    return (
      <Box bg="gray.1" p="sm" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
        <Group gap="xs" align="center" justify="space-between">
          <Badge variant="light" color="teal" leftSection={<IconCheck size={12} />}>
            Outlook connected
          </Badge>
          <Group gap="xs" align="center">
            {/* The status is cached for a minute, so a card raised because the token has just
                died can still read as connected — always leave a way to reconnect. */}
            <Button
              variant="subtle"
              color="orange"
              size="compact-xs"
              onClick={handleConnect}
              loading={isConnecting}
            >
              Reconnect
            </Button>
            <OutlookDisconnectButton />
          </Group>
        </Group>
      </Box>
    );
  }

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center">
          <IconMail size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Connect Outlook
          </Text>
        </Group>
        <Text size="textSm" c="gray.8">
          Siesta needs access to your Outlook account to read your emails. Once connected,
          you&apos;ll come straight back here and the conversation will carry on.
        </Text>
        <Group>
          <Button
            variant="filled"
            color="orange"
            size="sm"
            onClick={handleConnect}
            loading={isConnecting}
          >
            Connect Outlook
          </Button>
        </Group>
      </Flex>
    </Box>
  );
}
